/**
 * ownRailRoutes.ts — the routes that supply their OWN left rail, so the app's must not render.
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  ⛔ THIS IS DELIBERATELY NARROWER THAN "HIDE THE RAIL WHEN IT IS EMPTY".      ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 * The obvious rule is *hide the app rail wherever it has no sections*. That is the rule
 * Lis described, and it is probably the right one eventually. It is NOT the rule here,
 * and the reason is that I could not measure its blast radius.
 *
 * `dockKeyForPath` resolves a path to a module and then to a dock item; a route that
 * matches neither gets `contextSectionsForPath() === []` and renders a 256px rail with
 * nothing in it. **How many routes are in that state is a question I tried to answer
 * twice, statically, and got wrong twice** — both censuses claimed `/crm/contacts` has no
 * sections, which is plainly false, because the prefix table stores prefixes WITHOUT a
 * leading slash and my patterns assumed one. A rule whose reach I cannot state is a rule
 * that changes screens nobody looked at.
 *
 * So the shipped rule is the one whose reach is exactly one route: **hide the app rail on
 * paths that bring their own.** `/mail` is the only member, because Round Five gives mail a
 * rail with a Compose slot and a sync footer that a module's section list cannot express.
 *
 * ⚠️ **WHAT THIS AFFECTS, STATED PLAINLY:** on a matching route the app's `<aside>` does not
 * render, `<main>` loses its left margin, and `AnnouncementBanner` loses its left offset —
 * the three consumers of the rail's width. Nothing else reads it. (`Topbar` declares a
 * `collapsed` prop and never uses it; it is untouched.) On every non-matching route the
 * behaviour is byte-identical to before, which is what makes this safe to review on its own.
 *
 * ⚠️ **AND THE COLLAPSE TOGGLE STILL EXISTS.** The Topbar's hamburger still flips
 * `collapsed`, and on an own-rail route that now changes nothing visible. That is a real
 * loose end and it is named rather than hidden: the honest fix is for the toggle to know
 * about this state too, which is a Topbar change and out of this PR's scope.
 *
 * TO ADD A ROUTE: add its prefix here. To move to a genuine hide-when-empty rule, first
 * MEASURE which routes render an empty rail — by rendering them, not by grepping for
 * prefixes, because grepping for prefixes is what failed twice.
 */

/** Path prefixes whose pages render their own left rail. Matched like the module table. */
/**
 * ⛔ EMPTY SINCE 2026-10-06, AND THE EMPTINESS IS THE RULING.
 *
 * `'mail'` was the only member. Lis's two-column layout puts the REAL main sidebar on
 * `/mail` like every other page, with Mail's 216px folder column directly to its right —
 * so no route suppresses the app's rail any more.
 *
 * ⚠️ A RULE OVER AN EMPTY LIST CAN NEVER FIRE, which this repo rightly distrusts. It is
 * kept rather than deleted for one reason: the test beside it guards that THREE separate
 * places agree about the rail's width — the `<aside>`, `<main>`'s left margin and
 * `AnnouncementBanner`'s offset. Hide the rail and forget the margin and the page sits in a
 * 256px hole that looks like a layout bug. That invariant is still worth having, and it has
 * no other home today. **If a future change gives it one, delete this file.**
 */
const OWN_RAIL_PREFIXES: readonly string[] = [];

/**
 * Does this path supply its own left rail?
 *
 * Matching mirrors `moduleForPath`: the pathname is normalised without a leading slash and
 * a prefix matches the whole segment or a path beneath it. `/mailbox` must NOT match
 * `mail`, which is why this compares segment boundaries rather than using `startsWith`
 * alone — the bug this file's own header is about was a prefix that did not mean what it
 * looked like.
 */
export function routeSuppliesOwnRail(pathname: string): boolean {
  const path = pathname.replace(/^\/+/, '').replace(/\/+$/, '');
  return OWN_RAIL_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}
